import React, { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';
import {
  BarButton,
  IntegrationsBarContainer,
  NewTag,
  VerticalDivider,
  ChildrenArea,
  StyledTooltip,
  StyledDropDownTrigger,
  StyledAddMediaIcon,
  StyledChevronIcon,
  StyledDropdown,
  StyledListItem,
  SplitBarButton,
  ButtonContainer,
} from './styles';
import { SearchImagesModal } from './components/search-images-modal/SearchImagesModal';
import { IntegrationsService, IntegrationsContext } from './types';

/**
 *  COMPONENT: IDEAS INTEGRATIONS BAR
 */
export const IntegrationsBar = ({
  context,
  services,
  activeService,
  disableBar = false,
  children,
}: {
  context: IntegrationsContext;
  services: Array<IntegrationsService>;
  activeService: IntegrationsService | null;
  disableBar?: boolean;
  children?: React.ReactNode;
}) => {
  const [lastIntegrationUsed, setLastIntegrationUsed] = useState(
    localStorage.getItem('last_used_integration')
  );

  const [sawNewIndicator, setSawNewIndicator] = useState(
    !!localStorage.getItem('integration_bar_seen_new_indicator')
  );

  if (services.length === 0 && !children) return null;

  const storedIntegration = services.find(
    (e) => e.title === lastIntegrationUsed
  );

  const hasNewTags = !!services.find((e) => e.isNew) && !sawNewIndicator;

  const tooltipLabel = disableBar
    ? 'No more media can be attached'
    : 'Select media from integrations';

  const onServiceClick = (service) => {
    localStorage.setItem('last_used_integration', service.title);
    setLastIntegrationUsed(service.title);
    service.onClick?.();
  };

  const removeNewIndicator = () => {
    localStorage.setItem('integration_bar_seen_new_indicator', 'true');
    setSawNewIndicator(true);
  };

  return (
    <>
      <IntegrationsBarContainer data-testid="integrations-bar">
        <Tooltip.Provider>
          <ButtonContainer>
            {storedIntegration && (
              <Tooltip.Root delayDuration={0}>
                <Tooltip.Trigger asChild>
                  <SplitBarButton
                    onClick={() => storedIntegration?.onClick?.()}
                    disabled={disableBar}
                    aria-label={storedIntegration.label}
                    type="button"
                    darkerHover
                  >
                    {storedIntegration.icon?.({})}
                  </SplitBarButton>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <StyledTooltip data-state="instant-open" sideOffset={5}>
                    {disableBar
                      ? 'No more media can be attached'
                      : storedIntegration.label}
                  </StyledTooltip>
                </Tooltip.Portal>
              </Tooltip.Root>
            )}
            <DropdownMenu.Root onOpenChange={removeNewIndicator}>
              <StyledDropDownTrigger disabled={disableBar}>
                <Tooltip.Root delayDuration={0}>
                  <Tooltip.Trigger asChild>
                    <BarButton
                      aria-label="More integration options"
                      type="button"
                      slimPadding
                      hasNewTags={hasNewTags}
                      id="grouped-integrations-button"
                      data-testid="grouped-integrations-button"
                      disabled={disableBar}
                      darkerHover={!!storedIntegration}
                    >
                      {!storedIntegration && <StyledAddMediaIcon />}
                      <StyledChevronIcon />
                    </BarButton>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <StyledTooltip data-state="instant-open" sideOffset={5}>
                      {tooltipLabel}
                    </StyledTooltip>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </StyledDropDownTrigger>

              <DropdownMenu.Portal>
                <StyledDropdown align="start">
                  {services.map((service) => (
                    <StyledListItem
                      onClick={() => onServiceClick(service)}
                      key={service.id}
                      data-testid={`integrations-bar-${service.id}`}
                      disabled={disableBar}
                    >
                      {service.icon?.({}) ?? null}
                      {service.title}
                      {service.isNew && <NewTag isDropdownEnabled>New</NewTag>}
                    </StyledListItem>
                  ))}
                </StyledDropdown>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </ButtonContainer>

          {children && (
            <>
              <VerticalDivider />
              <ChildrenArea>{children}</ChildrenArea>
            </>
          )}
        </Tooltip.Provider>
      </IntegrationsBarContainer>

      {/* GENERAL MODAL FOR INTEGRATIONS */}
      <SearchImagesModal
        open={!!activeService?.openSearchImagesModal}
        handleClose={() => activeService?.onClose?.()}
        service={activeService}
        context={context}
      />
    </>
  );
};

IntegrationsBar.defaultProps = {
  disableBar: false,
  children: null,
};
